<template>
  <VaDropdown
    v-model="isOpen"
    :offset="[13, 0]"
    class="notification-dropdown"
    stick-to-edges
    :close-on-content-click="false"
  >
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary" aria-label="Thông báo">
        <VaBadge overlap :text="badgeText" :visible-empty="false" color="danger">
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:w-[400px] sm:h-auto">
      <section class="sm:max-h-[420px] p-2 overflow-auto">
        <div class="flex items-center justify-between px-2 pb-2">
          <span class="font-semibold">Thông báo</span>
          <VaButton v-if="unread > 0" preset="plain" size="small" @click="onMarkAll">Đánh dấu đã đọc hết</VaButton>
        </div>
        <VaInnerLoading :loading="loading">
          <p v-if="!loading && !items.length" class="text-sm text-[var(--va-secondary)] p-4 text-center">
            Chưa có thông báo
          </p>
          <button
            v-for="item in items"
            :key="item.id"
            type="button"
            class="notification-item"
            :class="{ unread: !item.readAt }"
            @click="onOpen(item)"
          >
            <VaIcon :name="iconOf(item.type).name" :color="iconOf(item.type).color" size="20px" class="mt-1" />
            <span class="flex-grow min-w-0 text-left">
              <span class="block text-sm font-semibold">{{ item.title }}</span>
              <span v-if="item.body" class="notification-body text-xs text-[var(--va-secondary)]">{{ item.body }}</span>
              <span class="block text-xs text-[var(--va-secondary)] mt-1">{{ relativeTime(item.createdAt) }}</span>
            </span>
            <span v-if="!item.readAt" class="unread-dot" aria-label="Chưa đọc" />
          </button>
        </VaInnerLoading>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vuestic-ui'
import VaIconNotification from '../../../icons/VaIconNotification.vue'
import {
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../../../data/pages/notifications'
import type { AppNotification } from '../../../../pages/social/types'

const POLL_MS = 60 * 1000

const router = useRouter()
const { init: notify } = useToast()

const isOpen = ref(false)
const loading = ref(false)
const unread = ref(0)
const items = ref<AppNotification[]>([])

const badgeText = computed(() => (unread.value > 99 ? '99+' : unread.value > 0 ? String(unread.value) : ''))

const ICONS: Record<string, { name: string; color: string }> = {
  SOCIAL_POST_PUBLISHED: { name: 'check_circle', color: 'success' },
  SOCIAL_POST_FAILED: { name: 'error', color: 'danger' },
  SOCIAL_POST_OUTCOME_UNKNOWN: { name: 'error', color: 'danger' },
  SOCIAL_CHANNEL_RECONNECT: { name: 'link_off', color: 'warning' },
  SOCIAL_POST_COMMENT: { name: 'chat', color: 'primary' },
  SOCIAL_PREVIEW_COMMENT: { name: 'chat', color: 'primary' },
  SOCIAL_POST_REVIEW: { name: 'rate_review', color: 'info' },
  SOCIAL_POST_APPROVED: { name: 'rate_review', color: 'success' },
  SOCIAL_POST_REJECTED: { name: 'rate_review', color: 'warning' },
}
const iconOf = (type: string) => ICONS[type] || { name: 'notifications', color: 'secondary' }

const rtf = new Intl.RelativeTimeFormat('vi', { numeric: 'auto' })
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86400000],
  ['month', 30 * 86400000],
  ['week', 7 * 86400000],
  ['day', 86400000],
  ['hour', 3600000],
  ['minute', 60000],
]
const relativeTime = (iso: string) => {
  const diff = new Date(iso).getTime() - Date.now()
  const unit = UNITS.find(([, ms]) => Math.abs(diff) >= ms)
  return unit ? rtf.format(Math.round(diff / unit[1]), unit[0]) : 'vừa xong'
}

const hasToken = () => !!localStorage.getItem('userToken')

const refreshCount = async () => {
  if (!hasToken() || document.hidden) return
  try {
    unread.value = await getUnreadCount()
  } catch (e) {
    // the bell must never disturb the page: try again on the next tick
  }
}

const load = async () => {
  if (!hasToken()) return
  loading.value = true
  try {
    const data = await getNotifications({ limit: 20 })
    items.value = data.items
    unread.value = data.unread
  } catch (e) {
    notify({ message: (e as Error).message, color: 'danger' })
  } finally {
    loading.value = false
  }
}

watch(isOpen, (open) => {
  if (open) load()
})

const onOpen = async (item: AppNotification) => {
  if (!item.readAt) {
    item.readAt = new Date().toISOString()
    unread.value = Math.max(0, unread.value - 1)
    markNotificationRead(item.id).catch(() => undefined)
  }
  isOpen.value = false
  if (item.link) router.push(item.link)
}

const onMarkAll = async () => {
  try {
    await markAllNotificationsRead()
    const now = new Date().toISOString()
    items.value.forEach((item) => {
      if (!item.readAt) item.readAt = now
    })
    unread.value = 0
  } catch (e) {
    notify({ message: (e as Error).message, color: 'danger' })
  }
}

let timer: ReturnType<typeof setInterval> | undefined
const onVisible = () => {
  if (!document.hidden) refreshCount()
}

onMounted(() => {
  refreshCount()
  timer = setInterval(refreshCount, POLL_MS)
  document.addEventListener('visibilitychange', onVisible)
})

onBeforeUnmount(() => {
  clearInterval(timer)
  document.removeEventListener('visibilitychange', onVisible)
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 8px;
  border-radius: 6px;

  &:hover {
    background: var(--va-background-element);
  }

  &.unread {
    background: var(--va-background-primary);
  }
}

.notification-body {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-line;
}

.unread-dot {
  width: 8px;
  height: 8px;
  margin-top: 8px;
  border-radius: 50%;
  background: var(--va-primary);
  flex-shrink: 0;
}
</style>
